<template>
  <section class="ann-hero">
    <!-- 场景图：按 globalStages 已解锁最高档切换，background 属性见 manifest hero.scene.states -->
    <div class="hero-scene" :class="scenePct === null ? '' : `scene-${scenePct}`"></div>

    <img class="hero-logo" :class="{ 'is-zh': isZhLogo }" :src="logoImg" alt="logo" />

    <!-- 能量条 -->
    <div class="power-panel">
      <div class="pp-title">{{ $t('ann2026.home.serverPower') }}</div>
      <img class="pp-flame" :src="powerFlameImg" alt="flame" />
      <div class="pp-value">{{ formattedFireCount }}</div>
      <div class="pp-track"></div>
      <div class="pp-fill" :style="fillStyle"></div>
      <template v-for="m in markers" :key="m.percent">
        <img
          class="pp-marker"
          :class="{ 'is-received': m.received, 'is-claimable': m.unlocked && !m.received }"
          :src="m.icon"
          :style="panelRectStyle(m.iconRect)"
          @click="onMarker(m)"
        />
        <div class="pp-marker-label" :class="{ top: m.percent === 100 }" :style="panelRectStyle(m.labelRect)">
          {{ m.percent }}%
        </div>
      </template>
      <div class="pp-bubble" :style="bubbleStyle">
        <div class="pp-bubble-text">{{ percent }}%</div>
        <img class="pp-bubble-arrow" :src="bubbleArrowImg" />
      </div>
    </div>

    <!-- 右侧悬浮按钮 -->
     <div class="hero-tool-wrap">
       <div
         v-for="tool in tools"
         :key="tool.key"
         class="hero-tool btn"
         :class="`hero-tool--${tool.key}`"
         @click="onTool(tool.key)"
       >
         <img class="hero-tool-icon" :src="tool.icon" />
       </div>
     </div>

    <!-- 主标题（多语言切图，垂直居中） -->
    <div class="hero-title-wrap">
      <img class="hero-title" :class="`is-${titleLang}`" :src="titleImg" alt="title" />
    </div>

    <!-- UID / 登出 -->
     <div class="hero-uid-wrap">
       <div v-if="props.isLoggedIn" class="hero-uid btn" @click="emit('logout')">
         {{ props.uid }} {{ $t('ann2026.hero.logout') }}
       </div>
       <div v-else class="hero-uid btn" @click="emit('login')">{{ $t('ann2026.hero.login') }}</div>
   
       <div class="hero-howtoplay flex-center btn" @click="emit('howToPlay')">
         {{ $t('ann2026.hero.howToPlay') }}
       </div>
     </div>
  </section>
</template>

<script setup>
/* eslint-disable */
import { computed, watch, defineProps, defineEmits, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

import logoDefaultImg from '@/assets/imgs/act/ann2026/hero/logo.png'
import logoZhImg from '@/assets/imgs/act/ann2026/hero/logo-zh.png'
import powerFlameImg from '@/assets/imgs/act/ann2026/hero/power-flame.png'
import bubbleArrowImg from '@/assets/imgs/act/ann2026/hero/bubble-arrow.png'
import marker100 from '@/assets/imgs/act/ann2026/hero/marker-100.png'
import marker75 from '@/assets/imgs/act/ann2026/hero/marker-75.png'
import marker50 from '@/assets/imgs/act/ann2026/hero/marker-50.png'
import marker25 from '@/assets/imgs/act/ann2026/hero/marker-25.png'
import toolRecharge from '@/assets/imgs/act/ann2026/hero/tool-recharge.png'
import toolService from '@/assets/imgs/act/ann2026/hero/tool-service.png'
import toolBack from '@/assets/imgs/act/ann2026/hero/tool-back.png'
import titleEn from '@/assets/imgs/act/ann2026/hero/title-en.png'
import titleZh from '@/assets/imgs/act/ann2026/hero/title-zh.png'
import titleVi from '@/assets/imgs/act/ann2026/hero/title-vi.png'
import titleId from '@/assets/imgs/act/ann2026/hero/title-id.png'
import titleTh from '@/assets/imgs/act/ann2026/hero/title-th.png'
import scene0 from '@/assets/imgs/act/ann2026/hero/scene-0.png'
import scene25 from '@/assets/imgs/act/ann2026/hero/scene-25.png'
import scene50 from '@/assets/imgs/act/ann2026/hero/scene-50.png'
import scene75 from '@/assets/imgs/act/ann2026/hero/scene-75.png'
import scene100 from '@/assets/imgs/act/ann2026/hero/scene-100.png'
import { useRouter } from 'vue-router'
import { aihelpLangOptions } from '@/config/lang'
import { loadScript } from '@/utils'
import { loadingService } from '@/utils/loadingService'

const router = useRouter()
const props = defineProps({
  // activityInfo.totalFireCount
  fireCount: { type: Number, default: 0 },
  // globalStages: [{ stage, percent, unlocked, received }]
  stages: { type: Array, default: () => [] },
  uid: { type: [String, Number], default: '' },
  isLoggedIn: { type: Boolean, default: false },
})
const emit = defineEmits(['login', 'logout', 'howToPlay', 'recharge', 'service', 'receive'])

const locale = useI18n().locale
const isZhLogo = computed(() => locale.value === 'hk' || locale.value === 'tw')
const logoImg = computed(() => (isZhLogo.value ? logoZhImg : logoDefaultImg))

// ===== 场景图 =====
const SCENE_STEPS = [0, 25, 50, 75, 100]
const sceneUrls = { 0: scene0, 25: scene25, 50: scene50, 75: scene75, 100: scene100 }

// 已解锁最高档（全服进度）
const percent = computed(() => {
  let max = 0
  props.stages.forEach((s) => {
    if (s.unlocked && Number(s.percent) > max) max = Number(s.percent)
  })
  return max
})

// 落到 0/25/50/75/100 档位；stages 未就绪时返回 null，避免先按 0% 白下一张大图
const scenePct = computed(() => {
  if (!props.stages.length) return null
  let step = 0
  SCENE_STEPS.forEach((v) => {
    if (percent.value >= v) step = v
  })
  return step
})

// 只加载当前档（CSS class 触发），预热下一档
watch(
  scenePct,
  (val) => {
    if (val === null) return
    const next = SCENE_STEPS[SCENE_STEPS.indexOf(val) + 1]
    if (next !== undefined) {
      const img = new Image()
      img.src = sceneUrls[next]
    }
  },
  { immediate: true },
)

// ===== 能量条 =====
// 设计稿页面绝对坐标 -> rem（基准 75.2，与 postcss rootValue 一致）
// 面板内动态元素统一减去面板原点 (20, 128)
const PANEL_ORIGIN = [20, 128]
const rem = (px) => `${+(px / 75.2).toFixed(5)}rem`
// 相对父元素原点的矩形（r 已是相对坐标）
const rectStyle = (r) => ({ left: rem(r[0]), top: rem(r[1]), width: rem(r[2]), height: rem(r[3]) })
const panelRectStyle = (r) => ({
  left: rem(r[0] - PANEL_ORIGIN[0]),
  top: rem(r[1] - PANEL_ORIGIN[1]),
  width: rem(r[2]),
  height: rem(r[3]),
})

const formattedFireCount = computed(() => Number(props.fireCount || 0).toLocaleString('en-US'))

// 填充底部固定 561（设计稿 fill 底边），满格高度 329（顶到 100% 刻度中心 232）
const FILL_BOTTOM = 561
const FILL_FULL_H = 329
const fillHeight = computed(() => (FILL_FULL_H * percent.value) / 100)
const fillTop = computed(() => FILL_BOTTOM - fillHeight.value)
const fillStyle = computed(() => panelRectStyle([51, fillTop.value, 27, fillHeight.value]))

// 气泡跟随填充顶部（设计稿示意位 top 442 对应 fill top 451，偏移 -9）
const bubbleStyle = computed(() => ({
  left: rem(3 - PANEL_ORIGIN[0]),
  top: rem(fillTop.value - 9 - PANEL_ORIGIN[1]),
}))

const MARKER_BASE = [
  { percent: 100, icon: marker100, iconRect: [77, 222, 18, 20], labelRect: [100, 233, 40, 11] },
  { percent: 75, icon: marker75, iconRect: [84, 307, 11, 12], labelRect: [101, 310, 31, 11] },
  { percent: 50, icon: marker50, iconRect: [84, 389, 11, 12], labelRect: [99, 392, 33, 11] },
  { percent: 25, icon: marker25, iconRect: [84, 471, 11, 12], labelRect: [100, 473, 32, 11] },
]

const markers = computed(() =>
  MARKER_BASE.map((m) => {
    const stageInfo = props.stages.find((s) => Number(s.percent) === m.percent)
    return {
      ...m,
      stage: stageInfo?.stage,
      unlocked: !!stageInfo?.unlocked,
      received: !!stageInfo?.received,
    }
  }),
)

// ===== 主标题 =====
const titleImg = computed(() => {
  switch (locale.value) {
    case 'vi':
      return titleVi
    case 'th':
      return titleTh
    case 'id':
      return titleId
    case 'tw':
      return titleZh
    case 'hk':
      return titleZh
    case 'en':
      return titleEn
    default:
      return titleEn
  }
})

const titleLang = computed(() => {
  switch (locale.value) {
    case 'vi':
      return 'vi'
    case 'th':
      return 'th'
    case 'id':
      return 'id'
    case 'tw':
      return 'zh'
    case 'hk':
      return 'zh'
    default:
      return 'en'
  }
})

// ===== 右侧悬浮按钮 =====
const tools = [
  { key: 'recharge', icon: toolRecharge, iconRect: [12.3, 11.7, 20, 24] },
  { key: 'service', icon: toolService, iconRect: [8.3, 8.5, 31, 27] },
  { key: 'backTop', icon: toolBack, iconRect: [11.7, 14.5, 21.2, 13.5] },
]

const handleCustom = async () => {
  const aihelpLang = aihelpLangOptions.find(item => item.appLang === locale.value)?.aihelpLang || 'en'
  if (!window.AIHelpSupport) {
    await loadScript('https://cdn.aihelp.net/webchatv5/aihelp.js')
  }
  const initConfig = {
    domain: 'miniwantechnologycolimited.aihelp.net',
    appId: 'miniwantechnologycolimited_platform_36bbfccee53e93eeaa51b18afe074c83',
    language: aihelpLang
  }
  console.log('locale.value', locale.value)
  const unwatch = watch(locale, () => {
    if (window.AIHelpSupport) {
      const language = aihelpLangOptions.find(item => item.appLang === locale.value)?.aihelpLang || 'en'
      window.AIHelpSupport.updateSDKLanguage(language)
    }
  })
  nextTick(() => {
    loadingService.show()
    window.AIHelpSupport.initialize(initConfig)
    window.AIHelpSupport.registerAsyncEventListener("initialization", function (jsonData) {
      console.log('jsonData', jsonData)
      if (jsonData.isSuccess) {
        window.AIHelpSupport.show("E002")
        loadingService.hide()
      }
    })
    window.AIHelpSupport.registerAsyncEventListener("sessionClose", function () {
      unwatch()
    })
  })
}

const onTool = (key) => {
  if (key === 'backTop') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }
  if (key === 'recharge') {
    const resolved = router.resolve({ name: 'Payment' })
    window.open(resolved.href, '_blank')
    return
  }
  if (key === 'service') {
    handleCustom()
    return
  }
  // emit(key)
}

const onMarker = (marker) => {
  // 未解锁 / 已领取 / 无 stage 信息不发起领取
  if (!marker?.unlocked || marker.received || marker.stage == null) return
  emit('receive', marker.stage)
}
</script>

<style lang="less" scoped>
// 区块范围：设计稿 0 ~ 848，坐标直接用 manifest 值，无偏移
// 整页三层背景统一由 .ann-page 渲染
.ann-hero {
  position: relative;
  width: 752px;
  height: 848px;
  background: transparent;
  overflow: hidden;

  > * {
    position: absolute;
  }
}

.flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

// ===== 场景图：不要用 background-size: 752px auto（会缩小 21%），数值见 manifest =====
.hero-scene {
  left: 0;
  top: 0;
  width: 752px;
  height: 795px;
  overflow: hidden;
  background-repeat: no-repeat;

  &.scene-0 {
    background-image: url('~@/assets/imgs/act/ann2026/hero/scene-0.png');
    background-position: -204px 0;
    background-size: 956px auto;
  }
  &.scene-25 {
    background-image: url('~@/assets/imgs/act/ann2026/hero/scene-25.png');
    background-position: -210px 0;
    background-size: 962px auto;
  }
  &.scene-50 {
    background-image: url('~@/assets/imgs/act/ann2026/hero/scene-50.png');
    background-position: -202px 0;
    background-size: 954px auto;
  }
  &.scene-75 {
    background-image: url('~@/assets/imgs/act/ann2026/hero/scene-75.png');
    background-position: -195px -40px;
    background-size: 947px auto;
  }
  &.scene-100 {
    background-image: url('~@/assets/imgs/act/ann2026/hero/scene-100.png');
    background-position: -200px -64px;
    background-size: 952px auto;
  }
}

.hero-logo {
  left: 10px;
  top: 15px;
  width: 94px;
  height: 43px;

  &.is-zh {
    width: 86px;
  }
}

// ===== 能量条 =====
.power-panel {
  left: 20px;
  top: 128px;
  width: 133px;
  height: 462px;
  background-image: url('~@/assets/imgs/act/ann2026/hero/power-bg.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;

  > * {
    position: absolute;
  }

  // 以下子元素坐标 = 页面绝对坐标 - 面板原点 (20, 128)
  .pp-title {
    left: 6px; // 26
    top: 33px; // 161
    display: flex;
    align-items: center;
    justify-content: center;
    width: 121px;
    min-height: 30px;
    color: #fff100;
    font-size: 13px;
    font-weight: 900;
    line-height: 15px;
    text-align: center;
    overflow-wrap: break-word;
  }

  .pp-flame {
    left: 25px; // 45
    top: 63px; // 191
    width: 11px;
    height: 29px;
  }

  .pp-value {
    left: 38px; // 58
    top: 71px; // 201
    width: 90px;
    height: 12px;
    line-height: 12px;
    font-size: 13px;
    font-weight: bold;
    color: #fff;
  }

  .pp-track {
    left: 28px; // 48
    top: 107px; // 235
    width: 36px;
    height: 329px;
    background-image: url('~@/assets/imgs/act/ann2026/hero/power-track.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .pp-fill {
    background-image: url('~@/assets/imgs/act/ann2026/hero/power-fill.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .pp-marker {
    transform: translateX(5px) scale(2);
    transform-origin: center center;

    &.is-claimable {
      cursor: pointer;
      animation: pp-marker-claimable 1.2s ease-in-out infinite;
    }

    &.is-received {
      filter: grayscale(100%);
      pointer-events: none;
      animation: none;
    }
  }

  @keyframes pp-marker-claimable {
    0%,
    100% {
      transform: translateX(5px) scale(2) rotate(0deg);
    }
    20% {
      transform: translateX(5px) scale(2.12) rotate(-8deg);
    }
    40% {
      transform: translateX(5px) scale(2.12) rotate(8deg);
    }
    60% {
      transform: translateX(5px) scale(2.12) rotate(-6deg);
    }
    80% {
      transform: translateX(5px) scale(2.06) rotate(4deg);
    }
  }

  .pp-marker-label {
    font-size: 13.7px;
    line-height: 11px;
    font-weight: bold;
    color: #fff;
    transform: translateX(8px);

    &.top {
      color: #ffff00;
    }
  }

  .pp-bubble {
    width: 49px;
    height: 27px;
    background-image: url('~@/assets/imgs/act/ann2026/hero/bubble-bg.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;

    .pp-bubble-text {
      font-size: 12.35px;
      color: #fff;
      text-align: center;
      position: static;
    }

    .pp-bubble-arrow {
      position: absolute;
      left: 40.2px; // 43.2 - 3
      top: 1.7px;
      width: 15.6px;
      height: 15.6px;
    }
  }
}

// ===== 右侧悬浮按钮 =====
.hero-tool-wrap {
  position: fixed;
  right: 20px;
  bottom: 100px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  .hero-tool {
    width: 44.6px;
    height: 44.6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    &:nth-child(1), &:nth-child(2) {
      &::before {
        content: '';
        display: block;
        position: absolute;
        left: 50%;
        bottom: -15px;
        transform: translateX(-50%);
        width: 6px;
        height: 6px;
        background-image: url('~@/assets/imgs/act/ann2026/hero/tool-dot.png');
        background-size: 100% 100%;
        background-repeat: no-repeat;
      }
    }
  
    &--recharge {
      // left: 693.7px;
      // top: 480.3px;
      // background-image: url('~@/assets/imgs/act/ann2026/hero/tool-circle-1.png');
      .hero-tool-icon {
        width: 80px !important;
        margin-bottom: 10px;
      }
    }
  
    &--service {
      // left: 693.7px;
      // top: 537.5px;
      background-image: url('~@/assets/imgs/act/ann2026/hero/tool-circle-2.png');
    }
  
    &--backTop {
      // left: 693.7px;
      // top: 595.4px;
      background-image: url('~@/assets/imgs/act/ann2026/hero/tool-circle-3.png');
    }
  
    background-size: 100% 100%;
    background-repeat: no-repeat;
  
    .hero-tool-icon {
      width: 30px;
      object-fit: contain;
      // position: absolute;
    }
  }
  
  .hero-tool-dot {
    width: 5.2px;
    height: 5.3px;
    background-image: url('~@/assets/imgs/act/ann2026/hero/tool-dot.png');
    background-size: 100% 100%;
  
    &--1 {
      left: 713.4px;
      top: 528.5px;
    }
  
    &--2 {
      left: 713.4px;
      top: 586px;
    }
  }
}

// ===== 主标题：各语言切图高度不同，容器内垂直居中 =====
.hero-title-wrap {
  // top: 574px;
  left: 50%;
  transform: translateX(-50%);
  bottom: 100px;

  .hero-title {
    height: 200px;
    object-fit: contain;
    // width: auto;

    // &.is-en {
    //   height: 203px;
    // }
    // &.is-zh {
    //   height: 314px;
    // }
    // &.is-vi {
    //   height: 231px;
    // }
    // &.is-id {
    //   height: 222px;
    // }
    // &.is-th {
    //   height: 216px;
    // }
  }
}
.hero-uid-wrap {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  .hero-uid {
    font-size: 22px;
    font-weight: bold;
    color: #fff;
    cursor: pointer;
    white-space: nowrap;
  }
  
  .hero-howtoplay {
    width: auto;
    min-width: 197px;
    max-width: 330px;
    height: 63px;
    padding: 0 28px;
    box-sizing: border-box;
    background-image: url('~@/assets/imgs/act/ann2026/hero/howtoplay-bg.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    font-size: 22px;
    font-weight: bold;
    color: #263278;
    cursor: pointer;
    white-space: nowrap;
  }
}
</style>
